import { useCallback } from 'react';
import { useQuery } from 'react-query';
import {
  AllSystemDomains,
  AllSystemState,
  getAllSystemState
} from 'src/Api/exchanges';
import { SYSTEM_ALL_STATE_KEY } from './useSystemKillSwitch';

export type PredictionDomain = keyof AllSystemDomains;

/**
 * Per-domain presence flags, every one of them nullable: `null` means the
 * backend did not report it. Nothing here is defaulted — an un-reported domain
 * stays unknown so callers can fail OPEN instead of gating on a guess.
 */
export interface DomainPresence {
  booted: boolean | null;
  /**
   * `matching` / `opportunities` only: their orchestrator has a READY replica.
   * The trading domains answer the same question with `nodeServing`, about their
   * `-node` pod.
   */
  serving: boolean | null;
  nodeReady: boolean | null;
  nodeServing: boolean | null;
  isRunning: boolean | null;
  armed: boolean | null;
  wanted: boolean | null;
  down: boolean | null;
}

const UNKNOWN_PRESENCE: DomainPresence = {
  booted: null,
  serving: null,
  nodeReady: null,
  nodeServing: null,
  isRunning: null,
  armed: null,
  wanted: null,
  down: null
};

const asBool = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null;

type RawDomainPresence = Record<string, unknown>;

/**
 * Typed read of the OPTIONAL `domains` block on `/api/system/all/state`. Uses
 * the kill switch's key + fetcher, so both observers share ONE cache entry and
 * one in-flight request; react-query 3 arms `refetchInterval` per observer, so
 * this does add its own 30s timer — kept deliberately, so the presence data
 * still refreshes on screens where the kill-switch button is not mounted.
 *
 * `domains` only exists on P5+ backends. When it is absent — or while the query
 * is loading, or after it errors — every field reads `null`, never `false`.
 *
 * `refetchIntervalMs` overrides the 30s default for one observer. A screen
 * whose PRIMARY on/off indicator is built on this data needs it: at 30s an
 * operator who just pressed Start watches a stale "OFF" for half a minute and
 * presses again. Per-observer intervals mean the faster caller only speeds up
 * the poll while its own screen is mounted.
 */
export function useDomainPresence(opts?: { refetchIntervalMs?: number }) {
  const query = useQuery<AllSystemState>(
    [SYSTEM_ALL_STATE_KEY],
    getAllSystemState,
    {
      refetchInterval: opts?.refetchIntervalMs ?? 30000,
      retry: 1
    }
  );

  const domains = query.data?.domains ?? null;

  /** The typed per-domain view. Unknown fields stay `null`. */
  const get = useCallback(
    (domain: PredictionDomain): DomainPresence => {
      const raw = (domains as Record<string, RawDomainPresence> | null)?.[
        domain
      ];
      if (!raw || typeof raw !== 'object') return UNKNOWN_PRESENCE;
      return {
        booted: asBool(raw.booted),
        serving: asBool(raw.serving),
        nodeReady: asBool(raw.nodeReady),
        nodeServing: asBool(raw.nodeServing),
        isRunning: asBool(raw.isRunning),
        armed: asBool(raw.armed),
        wanted: asBool(raw.wanted),
        down: asBool(raw.down)
      };
    },
    [domains]
  );

  /**
   * True ONLY when the backend positively reports the domain's rule-CRUD pod
   * as down. Unknown (`null`) is NOT down — the gate fails open.
   */
  const isNodeDown = useCallback(
    (domain: PredictionDomain): boolean => get(domain).nodeReady === false,
    [get]
  );

  return {
    get,
    isNodeDown,
    /**
     * Timestamp of the last SUCCESSFUL read (0 before the first one). Consumers
     * that debounce a state change count distinct polls with it — a re-render
     * alone must not be mistaken for fresh evidence.
     */
    dataUpdatedAt: query.dataUpdatedAt,
    /**
     * Whether the query has SETTLED at least once — succeeded or failed.
     * Distinct from `dataUpdatedAt > 0`, which cannot tell "the first request
     * is still in flight" from "the endpoint is not answering and never will",
     * so a consumer that treats the second as the first waits forever. This
     * query is served by ROOT, a different pod from the one a trading screen's
     * controls act on, so its outage must never disable those controls.
     *
     * Built on `errorUpdatedAt`, NOT `isError`: react-query 3 resets the status
     * to `loading` on every fetch while `dataUpdatedAt` is still 0
     * (`query.js` `case 'fetch'`), so `isError` oscillates false↔true across a
     * failing poll and the consumer would flip back to "still reading" for a
     * chunk of every cycle — and for a full cycle on every window-focus
     * refetch. `errorUpdatedAt` only ever moves forward (`case 'error'`).
     */
    firstAttemptSettled: query.dataUpdatedAt > 0 || query.errorUpdatedAt > 0
  };
}
